import React, { useContext } from 'react'
import { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Flex } from 'components/shared/flex'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { MeasureLedger, MeasureLedgerForm } from 'entities/measure_ledger'
import { BooleanRadioField } from 'components/shared/input'
import { ApiError } from 'utils/network/api_hooks'
import { GlobalStateContext } from 'contexts/global_state_context'
import { usePostMeasureLedgerApi, usePutMeasureLedgerApi } from 'api/measure_ledger'
import { DateField, InputField, TimePickerField } from 'components/shared/input'

const FormView = (form: Form<MeasureLedgerForm>, apiError: ApiError, isNew?: boolean) => {
    const globalState = useContext(GlobalStateContext)
    const isMonday = new Date(form.object.date + 'T00:00:00').getDay() === 1
    return (
        <Flex flexDirection='column'>
            <Flex>
                <DateField apiError={apiError} form={form} attr='date' required label='日付' disabled={!isNew} />
                <InputField form={form} attr={['staff', 'fullName']} disabled label='担当者' />
            </Flex>
            <Flex>
                <TimePickerField form={form} attr='startTime' label='開始時間' />
                <TimePickerField form={form} attr='endTime' label='終了時間' />
            </Flex>
            <Flex>
                <BooleanRadioField required apiError={apiError} form={form} attr='temperatureState' label='設定温度の状態' positiveLabel='異常なし' negativeLabel='異常あり' />
                <a style={{ marginTop: 37 }} href="/equipments" target="_blank" rel="noopener noreferrer">機器一覧</a>
            </Flex>
            <Flex>
                <BooleanRadioField required apiError={apiError} form={form} attr='equipmentState' label='検査機器の状態' positiveLabel='異常なし' negativeLabel='異常あり' />
                <a style={{ marginTop: 37 }} href="/equipments" target="_blank" rel="noopener noreferrer">機器一覧</a>
            </Flex>
            <Flex>
                <BooleanRadioField required apiError={apiError} form={form} attr='probeState' label='検査試薬の状態' positiveLabel='異常なし' negativeLabel='異常あり' />
                <a style={{ marginTop: 37 }} href="/ledgers/reagents" target="_blank" rel="noopener noreferrer">試薬管理台帳一覧</a>
            </Flex>
            <Flex>
                <BooleanRadioField required apiError={apiError} form={form} attr='qcReportState' label='精度管理の状態' positiveLabel='異常なし' negativeLabel='異常あり' />
                <a style={{ marginTop: 37 }} href="/reports/ct_charts" target="_blank" rel="noopener noreferrer">試薬管理台帳一覧</a>
            </Flex>
            <Flex>
                <BooleanRadioField required apiError={apiError} form={form} attr='resultReportState' label='検査結果報告書の未報告の有無' positiveLabel='異常なし' negativeLabel='異常あり' />
                <a style={{ marginTop: 37 }} href="/dashboards" target="_blank" rel="noopener noreferrer">ダッシュボード</a>
            </Flex>
            {
                isMonday && <Flex>
                    <InputField type='number' form={form} attr='warehousedCount' label='未検査チェック(月曜)' />
                    <a style={{ marginTop: 37, width: 200 }} href="/samples" target="_blank" rel="noopener noreferrer">未検査検体一覧</a>
                </Flex>
            }
            <InputField form={form} attr='note' label='特記事項' as={'textarea'} />
        </Flex >
    )
}

type NewMeasureLedgerFormProps = {
    onComplete?: () => void
    disabled?: boolean
}

export const NewMeasureLedgerForm = (props: NewMeasureLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostMeasureLedgerApi()
    const form = useForm<MeasureLedgerForm>({ startTime: '09:00', endTime: '18:00', probeState: true, equipmentState: true, qcReportState: true, temperatureState: true, resultReportState: true })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    const handleClose = () => {
        setShow(false)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <>
            <Button
                disabled={props.disabled}
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal fullscreen show={show} onHide={handleClose} onShow={() => form.update(f => {
                f.staff = globalState.user
                f.staffId = globalState.user.id
            })}>
                <Modal.Header closeButton>
                    <Modal.Title>測定作業日誌の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError, true)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditMeasureLedgerFormProps = {
    measureLedger: MeasureLedger
    setMeasureLedger?: React.Dispatch<React.SetStateAction<MeasureLedger>>
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditMeasureLedgerForm = (props: EditMeasureLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutMeasureLedgerApi()
    const form = useForm<MeasureLedgerForm>({})

    const handleUpdate = () => {
        api.execute(form)
        props.setMeasureLedger && props.setMeasureLedger(form.object)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    useEffectSkipFirst(() => {
        form.set({...props.measureLedger, staff: globalState.user, staffId: globalState.user.id})
    }, [props.measureLedger])

    const isChecked = props.measureLedger.staffId !== null
    return (
        <Modal fullscreen show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>{`測定作業日誌の${isChecked ? "編集" : "確認"}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {FormView(form, api.apiError)}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    {`${isChecked ? "編集" : "確認"}`}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
